import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { HttpInterceptorFn, HttpErrorResponse, HttpEvent, HttpRequest } from '@angular/common/http';
import { inject, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, switchMap, filter, take, finalize } from 'rxjs/operators';
import { throwError, EMPTY, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthService } from '../../auth/components/auth.service';
import { ToastService } from '../../shared/services/toast.service';

/**
 * Check if the URL is for a booking-related endpoint that should handle 401 errors in-context
 * rather than automatically redirecting to login.
 *
 * Note: the recently-locked-down endpoints (courts, categories, subscriptions,
 * courses, organization-settings public info) are intentionally NOT in this
 * list — their routes are already guarded by authGuard so a token should
 * always be present, and if one does 401 it should go through the normal
 * refresh flow below rather than be silently swallowed.
 */
function isBookingRelatedEndpoint(url: string): boolean {
  const bookingEndpoints = [
    '/holds',
    '/bookings',
    '/payments',
    '/stripe'
  ];
  
  return bookingEndpoints.some(endpoint => url.includes(endpoint));
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
    const platformId = inject(PLATFORM_ID);
    const authService = inject(AuthService);
    const router = inject(Router);
    const toastService = inject(ToastService);

    // Check if the platform is server or browser due to SSR
    if (isPlatformBrowser(platformId)) {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        if (token && req.url.startsWith(environment.apiUrl)) {
          // Don't add expired token to refresh request
          if (req.url.includes('/auth/refresh')) {
            // Refresh endpoint expects token in body, not header
            return next(req);
          }
          req = req.clone({
            headers: req.headers.set('Authorization', `Bearer ${token}`),
          });
        }
      }
    
      return next(req).pipe(
        catchError((error: HttpErrorResponse) => {
          // Don't retry refresh endpoint to avoid infinite loops
          if (error.status === 401 && !req.url.includes('/auth/')) {
            // Check if this is a booking-related endpoint that should handle 401 in-context
            if (isBookingRelatedEndpoint(req.url)) {
              // For booking endpoints, pass through the 401 error so booking modal can handle it
              return throwError(() => error);
            }
            // For other endpoints, handle with automatic token refresh/redirect
            return handle401Error(req, next, authService, router, toastService);
          }
          return throwError(() => error);
        })
      );
};

function handle401Error(req: HttpRequest<any>, next: any, authService: AuthService, router: Router, toastService: ToastService): Observable<HttpEvent<any>> {
  // If already refreshing, wait for the result with proper error handling
  if (authService.isRefreshing) {
    return authService.getRefreshTokenSubject().pipe(
      filter(response => response !== null),
      take(1),
      switchMap((response): Observable<HttpEvent<any>> => {
        if (response && response.accessToken) {
          const newReq = req.clone({
            headers: req.headers.set('Authorization', `Bearer ${response.accessToken}`)
          });
          return next(newReq);
        }
        throw new Error('Token refresh failed - no access token');
      }),
      catchError((error): Observable<HttpEvent<any>> => {
        authService.logout();
        // Explain the redirect — without this, a token expiring mid-session
        // (now the common case since most routes require auth) looks like a
        // silent bug rather than an expected session-expiry bounce.
        toastService.info('Deine Sitzung ist abgelaufen. Bitte melde dich erneut an.');
        router.navigate(['/login'], { queryParams: { returnUrl: router.url } });
        return EMPTY;
      })
    );
  }

  // Try to refresh the token with improved error handling
  return authService.refreshToken().pipe(
    switchMap((response): Observable<HttpEvent<any>> => {
      if (response && response.accessToken) {
        const newReq = req.clone({
          headers: req.headers.set('Authorization', `Bearer ${response.accessToken}`)
        });
        return next(newReq);
      }
      throw new Error('Token refresh failed - no access token in response');
    }),
    catchError((error): Observable<HttpEvent<any>> => {
      // If refresh fails, logout and redirect to login
      authService.logout();
      toastService.info('Deine Sitzung ist abgelaufen. Bitte melde dich erneut an.');
      router.navigate(['/login'], { queryParams: { returnUrl: router.url } });
      return EMPTY;
    })
  );
}
